import * as React from "react";
import Card from "react-bootstrap/Card";
import Spinner from "react-bootstrap/Spinner";
import Table from "react-bootstrap/Table";
import Button from "react-bootstrap/Button";
import { gettext } from "i18n";
import apiClient from "api";
import LoadCalculationChart from "./LoadCalculationChart";

interface LoadCalculationWidgetProps {
  title: string;
  widgetId: string;
  dashboardId: number;
  metaData: {
    cacheDurationHours: number;
    reportingFrequency: string;
    showResultsIn: string;
    flowDataSourceUUID: string;
    flowUnit: string;
    numberOfLoadParameters: number;
    loadParameters: Array<{
      name: string;
      concentrationDataSourceUUID: string;
      calculationMethod: number;
      detectionLimit: number;
      belowDetectionLimitHandling: number;
      missingValuesHandling: number;
      unit?: string;
    }>;
    dateRange?: string[];
  };
  startDate: Date;
  endDate: Date;
  preview?: boolean;
  widgetConfig?: {
    key: string;
    metaData: Record<string, any>;
    dateRange?: string[];
  };
}

const LoadCalculationWidget: React.FC<LoadCalculationWidgetProps> = ({
  title,
  widgetId,
  dashboardId,
  metaData,
  preview = false,
  widgetConfig,
}) => {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<any>(null);

  const fetchCalculation = React.useCallback(
    async (forceRecalculate: boolean = false) => {
      setLoading(true);
      setError(null);

      try {
        const payload: any = {
          dashboardId,
          widgetId,
          forceRecalculate,
        };

        if (widgetConfig) {
          payload.widgetConfig = {
            metaData: {
              widgetId,
              ...metaData,
            },
            dateRange: widgetConfig.dateRange ??
              metaData.dateRange ?? [
                new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
                new Date().toISOString(),
              ],
          };
        }

        const response = await apiClient.request(
          "/organization-member/load-calculation",
          {
            method: "POST",
            data: payload,
          },
        );

        if (response.success) {
          setResult(response);
        } else {
          setError(response.error || gettext("Calculation failed"));
        }
      } catch (err: any) {
        setError(err.message || gettext("Error loading calculation results"));
      } finally {
        setLoading(false);
      }
    },
    [dashboardId, widgetId, metaData, widgetConfig],
  );

  React.useEffect(() => {
    fetchCalculation(false);
  }, [fetchCalculation]);

  const handleRecalculate = () => {
    fetchCalculation(true);
  };

  if (loading) {
    return (
      <Card>
        <Card.Body className="text-center py-5">
          <Spinner animation="border" role="status" className="mb-3" />
          <div>{gettext("Calculating load...")}</div>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Card.Body>
          <div className="text-danger mb-3">{error}</div>
          <Button variant="primary" size="sm" onClick={handleRecalculate}>
            {gettext("Retry")}
          </Button>
        </Card.Body>
      </Card>
    );
  }

  if (!result?.success || !result?.data) {
    return (
      <Card>
        <Card.Body>
          <div className="text-danger mb-3">
            {result?.error || gettext("No calculation results available")}
          </div>
          <Button variant="primary" size="sm" onClick={handleRecalculate}>
            {gettext("Calculate")}
          </Button>
        </Card.Body>
      </Card>
    );
  }

  const { periods, totalLoads, totalCumulativeFlow } = result.data;
  const calculatedAt = result.calculatedAt;
  const showChart =
    metaData.showResultsIn === "line_graph" ||
    metaData.showResultsIn === "bar_graph";

  const renderContent = () => {
    if (showChart) {
      return (
        <LoadCalculationChart
          periods={periods}
          loadParameters={metaData.loadParameters}
          showResultsIn={metaData.showResultsIn as "line_graph" | "bar_graph"}
        />
      );
    }

    return (
      <Table striped bordered hover responsive size="sm">
        <thead>
          <tr>
            <th>{gettext("Parameter")}</th>
            {periods?.map((period: any) => (
              <th key={period.key} className="text-center">
                {period.label}
              </th>
            ))}
            <th className="text-center">{gettext("Total")}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <strong>{gettext("Cumulative flow")}</strong>
              <br />
              <small className="text-muted">({metaData.flowUnit})</small>
            </td>
            {periods?.map((period: any) => (
              <td key={`flow_${period.key}`} className="text-end">
                {period.cumulativeFlow?.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </td>
            ))}
            <td className="text-end">
              <strong>
                {totalCumulativeFlow?.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </strong>
            </td>
          </tr>
          {metaData.loadParameters?.map((param, paramIndex) => (
            <React.Fragment key={`param_${paramIndex}`}>
              <tr className="table-secondary">
                <td colSpan={periods?.length + 2}>
                  <strong>
                    {param.name || `${gettext("Parameter")} ${paramIndex + 1}`}
                  </strong>
                </td>
              </tr>
              <tr>
                <td>
                  {gettext("Average concentration")}
                  <br />
                  <small className="text-muted">({param.unit || "mg/l"})</small>
                </td>
                {periods?.map((period: any) => {
                  const pData = period.loadParameters?.[paramIndex];
                  return (
                    <td
                      key={`avg_${paramIndex}_${period.key}`}
                      className={`text-end ${pData?.interpolated ? "text-warning" : ""}`}
                      title={
                        pData?.interpolated ? gettext("Interpolated value") : ""
                      }
                    >
                      {pData?.averageConcentration?.toFixed(2)}
                      {pData?.interpolated && " *"}
                    </td>
                  );
                })}
                <td className="text-end">-</td>
              </tr>
              <tr>
                <td>{gettext("Available samples")}</td>
                {periods?.map((period: any) => (
                  <td
                    key={`samples_${paramIndex}_${period.key}`}
                    className="text-end"
                  >
                    {period.loadParameters?.[paramIndex]?.sampleCount}
                  </td>
                ))}
                <td className="text-end">-</td>
              </tr>
              <tr className="table-primary">
                <td>
                  <strong>
                    {gettext("Total load")} (
                    {param.name || `${gettext("Parameter")} ${paramIndex + 1}`})
                  </strong>
                  <br />
                  <small className="text-muted">[kg]</small>
                </td>
                {periods?.map((period: any) => (
                  <td
                    key={`load_${paramIndex}_${period.key}`}
                    className="text-end"
                  >
                    <strong>
                      {period.loadParameters?.[paramIndex]?.totalLoad?.toFixed(
                        2,
                      )}
                    </strong>
                  </td>
                ))}
                <td className="text-end">
                  <strong>{totalLoads?.[paramIndex]?.toFixed(2)}</strong>
                </td>
              </tr>
            </React.Fragment>
          ))}
        </tbody>
      </Table>
    );
  };

  if (preview) {
    return (
      <div className="load-calculation-preview">
        {renderContent()}
        {!showChart && (
          <div className="d-flex justify-content-between align-items-center mt-2">
            {calculatedAt && (
              <small className="text-muted">
                {gettext("Calculated")}:{" "}
                {new Date(calculatedAt).toLocaleString()}
              </small>
            )}
            <small className="text-muted">
              * {gettext("Interpolated values")}
            </small>
          </div>
        )}
      </div>
    );
  }

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div>
          <h5 className="mb-0">{title || gettext("Load Calculation")}</h5>
          {calculatedAt && (
            <small className="text-muted">
              {gettext("Calculated")}: {new Date(calculatedAt).toLocaleString()}
            </small>
          )}
        </div>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={handleRecalculate}
          disabled={loading}
        >
          {gettext("Recalculate")}
        </Button>
      </Card.Header>
      <Card.Body>
        {renderContent()}
        {!showChart && (
          <div className="mt-2">
            <small className="text-muted">
              * {gettext("Interpolated values")}
            </small>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default LoadCalculationWidget;
